import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { NotificationBell } from '@/components/notifications'
import { UserMenu } from '@/components/user'
import { useAuth } from '@/auth'
import { useProfile } from '@/hooks/useProfile'
import { useTheme } from '@/hooks/useTheme'
import { Trophy, Menu, Swords, Layers, Users, Home, User, LogOut, LogIn, Moon, Sun } from 'lucide-react'

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

export function Header() {
  const { isAuthenticated, login, logout } = useAuth()
  const { profile } = useProfile()
  const { theme, toggleTheme } = useTheme()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  // Vite's `import.meta.env.PROD` indicates a production *build* (vite build),
  // not the deployment environment. Our non-prod deployments are still built in
  // production mode, so use an explicit stage flag instead.
  const stage = (import.meta.env.VITE_STAGE as string | undefined) || (import.meta.env.PROD ? 'prod' : 'dev')
  const isProd = stage === 'prod'
  // Show BETA for production, DEV for development
  const envLabel = isProd ? 'BETA' : stage.toUpperCase()
  
  // Check if we're on a group-related page
  const isGroupPage = location.pathname === '/groups' || location.pathname.startsWith('/group')
  const isMatchesPage = location.pathname === '/matches'
  const isLeaguesPage = location.pathname === '/leagues'
  
  const navLinkClass = (isActive: boolean) =>
    `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-9 px-4 py-2 ${
      isActive ? 'bg-secondary text-secondary-foreground' : 'hover:bg-accent hover:text-accent-foreground'
    }`
  
  const mobileNavLinkClass = (isActive: boolean) =>
    `inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-10 px-4 py-2 w-full ${
      isActive ? 'bg-secondary text-secondary-foreground' : 'hover:bg-accent hover:text-accent-foreground'
    }`

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-14 items-center px-4">
        <Link to="/" className="flex items-center gap-2">
          <Trophy className="h-6 w-6 text-primary" />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold leading-tight">Badminton Leagues</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide ${
                isProd 
                  ? 'bg-primary/20 text-primary' 
                  : 'bg-destructive text-destructive-foreground'
              }`}>
                {envLabel}
              </span>
            </div>
            <span className="text-[10px] text-muted-foreground leading-none hidden sm:block">by Badminton Scores</span>
          </div>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="ml-auto hidden sm:flex items-center gap-2">
          {isAuthenticated && (
            <>
              <Link to="/groups" className={navLinkClass(isGroupPage)}>
                <Users className="h-4 w-4" />
                My Groups
              </Link>
              <Link to="/leagues" className={navLinkClass(isLeaguesPage)}>
                <Layers className="h-4 w-4" />
                My Leagues
              </Link>
              <Link to="/matches" className={navLinkClass(isMatchesPage)}>
                <Swords className="h-4 w-4" />
                My Matches
              </Link>
            </>
          )}
          <NotificationBell />
          <UserMenu />
        </nav>

        {/* Mobile Navigation */}
        <div className="ml-auto flex sm:hidden items-center gap-2">
          <NotificationBell />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] flex flex-col">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-primary" />
                  Menu
                </SheetTitle>
              </SheetHeader>
              
              {/* Navigation Links */}
              <nav className="flex flex-col gap-1 mt-6">
                <Link to="/" className={mobileNavLinkClass(location.pathname === '/')} onClick={() => setMobileMenuOpen(false)}>
                  <Home className="h-4 w-4" />
                  Home
                </Link>
                {isAuthenticated && (
                  <>
                    <Link to="/groups" className={mobileNavLinkClass(isGroupPage)} onClick={() => setMobileMenuOpen(false)}>
                      <Users className="h-4 w-4" />
                      My Groups
                    </Link>
                    <Link to="/leagues" className={mobileNavLinkClass(isLeaguesPage)} onClick={() => setMobileMenuOpen(false)}>
                      <Layers className="h-4 w-4" />
                      My Leagues
                    </Link>
                    <Link to="/matches" className={mobileNavLinkClass(isMatchesPage)} onClick={() => setMobileMenuOpen(false)}>
                      <Swords className="h-4 w-4" />
                      My Matches
                    </Link>
                  </>
                )}
              </nav>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Bottom Section */}
              <div className="border-t pt-3 space-y-2">
                {isAuthenticated && profile ? (
                  <>
                    {/* User Info Row */}
                    <div className="flex items-center gap-2.5 px-2 py-1.5">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={profile.picture} alt={profile.displayName} />
                        <AvatarFallback className="text-xs">{getInitials(profile.displayName)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate leading-tight">{profile.displayName}</p>
                        <p className="text-[11px] text-muted-foreground truncate leading-tight">{profile.email}</p>
                      </div>
                    </div>
                    
                    {/* Actions Row */}
                    <div className="flex items-center gap-1 px-1">
                      <Link 
                        to="/profile" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex-1 flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium hover:bg-accent transition-colors"
                      >
                        <User className="h-3.5 w-3.5" />
                        Profile
                      </Link>
                      <div className="w-px h-4 bg-border" />
                      <button
                        onClick={toggleTheme}
                        className="flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium hover:bg-accent transition-colors"
                      >
                        {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                        {theme === 'dark' ? 'Light' : 'Dark'}
                      </button>
                      <div className="w-px h-4 bg-border" />
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false)
                          logout()
                        }}
                        className="flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <LogOut className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 px-1">
                    <button
                      onClick={toggleTheme}
                      className="flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent transition-colors"
                    >
                      {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                    <Button 
                      onClick={() => {
                        setMobileMenuOpen(false)
                        login()
                      }}
                      className="flex-1"
                      size="sm"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Sign In
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
